"use client";

import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { useEffect, useState } from "react";
import {
  BadmintonDisplay,
  type BadmintonTemplateData,
} from "@/components/posts/activity-templates/badminton-template";
import {
  BasketballDisplay,
  type BasketballTemplateData,
} from "@/components/posts/activity-templates/basketball-template";
import { ClimbingDisplay } from "@/components/posts/activity-templates/climbing-template";
import {
  CricketDisplay,
  type CricketTemplateData,
} from "@/components/posts/activity-templates/cricket-template";
import { DayRetreatsDisplay } from "@/components/posts/activity-templates/day-retreats-template";
import {
  FootballDisplay,
  type FootballTemplateData,
} from "@/components/posts/activity-templates/football-template";
import { GolfDisplay } from "@/components/posts/activity-templates/golf-template";
import { HyroxDisplay } from "@/components/posts/activity-templates/hyrox-template";
import { MixedMartialArtsDisplay } from "@/components/posts/activity-templates/mixed-martial-arts-template";
import {
  PadelDisplay,
  type PadelTemplateData,
} from "@/components/posts/activity-templates/padel-template";
import {
  PickleballDisplay,
  type PickleballTemplateData,
} from "@/components/posts/activity-templates/pickleball-template";
import { PilatesDisplay } from "@/components/posts/activity-templates/pilates-template";
import { RecoveryDisplay } from "@/components/posts/activity-templates/recovery-template";
import {
  SnookerDisplay,
  type SnookerTemplateData,
} from "@/components/posts/activity-templates/snooker-template";
import {
  SquashDisplay,
  type SquashTemplateData,
} from "@/components/posts/activity-templates/squash-template";
import {
  TableTennisDisplay,
  type TableTennisTemplateData,
} from "@/components/posts/activity-templates/table-tennis-template";
import {
  TennisDisplay,
  type TennisTemplateData,
} from "@/components/posts/activity-templates/tennis-template";
import {
  ThrowballDisplay,
  type ThrowballTemplateData,
} from "@/components/posts/activity-templates/throwball-template";
import { TrekkingDisplay } from "@/components/posts/activity-templates/trekking-template";
import {
  VolleyballDisplay,
  type VolleyballTemplateData,
} from "@/components/posts/activity-templates/volleyball-template";
import { YogaDisplay } from "@/components/posts/activity-templates/yoga-template";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import type { ActivitySessionFormat } from "@/db/schema/activity-session";
import { cn } from "@/lib/utils";

// ──────────────────────────────────────────────────────────────────────────────
// TYPES
// ──────────────────────────────────────────────────────────────────────────────

export type TeamParticipant = {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  username: string | null;
  team: "team1" | "team2" | null;
  isCreator?: boolean;
};

type ActivitySessionCourtPreviewProps = {
  dndContextId?: string;
  activityId: string;
  sessionFormat: ActivitySessionFormat | null;
  participants: TeamParticipant[];
  scoreData?: Record<string, unknown> | null;
  isEditable?: boolean;
  disableScrollWhileDragging?: boolean;
  onTeamChange?: (userId: string, team: "team1" | "team2") => void;
};

// Activities that have court/field display components
const DISPLAY_ACTIVITIES: Record<
  string,
  React.ComponentType<{ data: unknown; participants: DisplayParticipant[] }>
> = {
  tennis: TennisDisplay as React.ComponentType<{
    data: unknown;
    participants: DisplayParticipant[];
  }>,
  badminton: BadmintonDisplay as React.ComponentType<{
    data: unknown;
    participants: DisplayParticipant[];
  }>,
  football: FootballDisplay as React.ComponentType<{
    data: unknown;
    participants: DisplayParticipant[];
  }>,
  basketball: BasketballDisplay as React.ComponentType<{
    data: unknown;
    participants: DisplayParticipant[];
  }>,
  squash: SquashDisplay as React.ComponentType<{
    data: unknown;
    participants: DisplayParticipant[];
  }>,
  snooker: SnookerDisplay as React.ComponentType<{
    data: unknown;
    participants: DisplayParticipant[];
  }>,
  padel: PadelDisplay as React.ComponentType<{
    data: unknown;
    participants: DisplayParticipant[];
  }>,
  pickleball: PickleballDisplay as React.ComponentType<{
    data: unknown;
    participants: DisplayParticipant[];
  }>,
  "table-tennis": TableTennisDisplay as React.ComponentType<{
    data: unknown;
    participants: DisplayParticipant[];
  }>,
  tabletennis: TableTennisDisplay as React.ComponentType<{
    data: unknown;
    participants: DisplayParticipant[];
  }>,
  cricket: CricketDisplay as React.ComponentType<{
    data: unknown;
    participants: DisplayParticipant[];
  }>,
  volleyball: VolleyballDisplay as React.ComponentType<{
    data: unknown;
    participants: DisplayParticipant[];
  }>,
  throwball: ThrowballDisplay as React.ComponentType<{
    data: unknown;
    participants: DisplayParticipant[];
  }>,
  yoga: YogaDisplay as React.ComponentType<{
    data: unknown;
    participants: DisplayParticipant[];
  }>,
  hyrox: HyroxDisplay as React.ComponentType<{
    data: unknown;
    participants: DisplayParticipant[];
  }>,
  pilates: PilatesDisplay as React.ComponentType<{
    data: unknown;
    participants: DisplayParticipant[];
  }>,
  recovery: RecoveryDisplay as React.ComponentType<{
    data: unknown;
    participants: DisplayParticipant[];
  }>,
  "day-retreats": DayRetreatsDisplay as React.ComponentType<{
    data: unknown;
    participants: DisplayParticipant[];
  }>,
  dayretreats: DayRetreatsDisplay as React.ComponentType<{
    data: unknown;
    participants: DisplayParticipant[];
  }>,
  "mixed-martial-arts": MixedMartialArtsDisplay as React.ComponentType<{
    data: unknown;
    participants: DisplayParticipant[];
  }>,
  mixedmartialarts: MixedMartialArtsDisplay as React.ComponentType<{
    data: unknown;
    participants: DisplayParticipant[];
  }>,
  golf: GolfDisplay as React.ComponentType<{
    data: unknown;
    participants: DisplayParticipant[];
  }>,
  climbing: ClimbingDisplay as React.ComponentType<{
    data: unknown;
    participants: DisplayParticipant[];
  }>,
  trekking: TrekkingDisplay as React.ComponentType<{
    data: unknown;
    participants: DisplayParticipant[];
  }>,
};

type DisplayParticipant = {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  username: string | null;
};

const normalizeActivityId = (id: string) =>
  id.toLowerCase().replace(/[^a-z-]/g, "");

const INDIVIDUAL_DISPLAY_ACTIVITY_IDS = new Set([
  "yoga",
  "hyrox",
  "pilates",
  "recovery",
  "dayretreats",
  "mixedmartialarts",
  "golf",
  "climbing",
  "trekking",
]);

export function isActivitySessionCourtPreviewSupported(
  activityId: string,
): boolean {
  const normalized = normalizeActivityId(activityId).replace(/-/g, "");
  return (
    Object.keys(DISPLAY_ACTIVITIES).find(
      (key) => normalizeActivityId(key).replace(/-/g, "") === normalized,
    ) != null
  );
}

export function shouldUseActivitySessionCourtPreview(
  activityId: string,
  sessionFormat: ActivitySessionFormat | null,
): boolean {
  if (!isActivitySessionCourtPreviewSupported(activityId)) return false;

  const normalizedLookupId = normalizeActivityId(activityId).replace(/-/g, "");

  if (INDIVIDUAL_DISPLAY_ACTIVITY_IDS.has(normalizedLookupId)) return true;

  return sessionFormat != null && sessionFormat !== "group";
}

// ──────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ──────────────────────────────────────────────────────────────────────────────

export function ActivitySessionCourtPreview({
  dndContextId,
  activityId,
  sessionFormat,
  participants,
  scoreData = null,
  isEditable = false,
  disableScrollWhileDragging = true,
  onTeamChange,
}: ActivitySessionCourtPreviewProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const normalizedId = normalizeActivityId(activityId);
  const normalizedLookupId = normalizedId.replace(/-/g, "");

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
  );
  const isDragging = activeId != null;

  useEffect(() => {
    if (!disableScrollWhileDragging || !isDragging || !isEditable) return;

    const html = document.documentElement;
    const body = document.body;
    const previousHtmlOverscroll = html.style.overscrollBehavior;
    const previousHtmlTouchAction = html.style.touchAction;
    const previousBodyOverflow = body.style.overflow;
    const previousBodyOverscroll = body.style.overscrollBehavior;
    const previousBodyTouchAction = body.style.touchAction;
    const preventScroll = (event: Event) => {
      event.preventDefault();
    };

    html.style.overscrollBehavior = "none";
    html.style.touchAction = "none";
    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";
    body.style.touchAction = "none";
    window.addEventListener("touchmove", preventScroll, { passive: false });
    window.addEventListener("wheel", preventScroll, { passive: false });

    return () => {
      html.style.overscrollBehavior = previousHtmlOverscroll;
      html.style.touchAction = previousHtmlTouchAction;
      body.style.overflow = previousBodyOverflow;
      body.style.overscrollBehavior = previousBodyOverscroll;
      body.style.touchAction = previousBodyTouchAction;
      window.removeEventListener("touchmove", preventScroll);
      window.removeEventListener("wheel", preventScroll);
    };
  }, [disableScrollWhileDragging, isDragging, isEditable]);

  // Find the display component for this activity
  const DisplayComponent = Object.entries(DISPLAY_ACTIVITIES).find(
    ([key]) =>
      normalizeActivityId(key).replace(/-/g, "") === normalizedLookupId,
  )?.[1];

  if (!DisplayComponent) {
    return <SimpleParticipantList participants={participants} />;
  }

  const displayParticipants: DisplayParticipant[] = participants.map((p) => ({
    id: p.id,
    displayName: p.displayName,
    avatarUrl: p.avatarUrl,
    username: p.username,
  }));

  if (INDIVIDUAL_DISPLAY_ACTIVITY_IDS.has(normalizedLookupId)) {
    return (
      <DisplayComponent
        data={scoreData ?? null}
        participants={displayParticipants}
      />
    );
  }

  if (!sessionFormat || sessionFormat === "group") {
    return <SimpleParticipantList participants={participants} />;
  }

  // Build positions map from team assignments
  const positions: Record<string, "team1" | "team2"> = {};
  for (const p of participants) {
    if (p.team) {
      positions[p.id] = p.team;
    }
  }

  // Build data object for display component
  const displayData = buildDisplayData(
    normalizedId,
    sessionFormat,
    positions,
    scoreData,
  );

  const activeParticipant = participants.find((p) => p.id === activeId);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || !isEditable || !onTeamChange) return;

    const userId = active.id as string;
    const targetTeam = over.id as "team1" | "team2";

    if (targetTeam === "team1" || targetTeam === "team2") {
      onTeamChange(userId, targetTeam);
    }
  };

  const yourTeam = participants.filter((p) => p.team === "team1");
  const opponentTeam = participants.filter((p) => p.team === "team2");
  const unassigned = participants.filter((p) => !p.team);

  return (
    <div className="space-y-4">
      {/* Court/Field Preview - reuses existing template display */}
      <DisplayComponent data={displayData} participants={displayParticipants} />

      {/* Team Assignment UI - only shown when editable */}
      {isEditable && (
        <DndContext
          id={dndContextId}
          sensors={sensors}
          autoScroll={!disableScrollWhileDragging}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveId(null)}
        >
          <div className="space-y-3">
            <p className="text-center text-sm text-text-secondary">
              Drag players to assign teams
            </p>

            <div className="grid grid-cols-2 gap-3">
              {/* Team 1 Drop Zone */}
              <TeamDropZone
                id="team1"
                label="Team 1"
                participants={yourTeam}
                variant="your"
              />

              {/* Opponent Drop Zone */}
              <TeamDropZone
                id="team2"
                label="Team 2"
                participants={opponentTeam}
                variant="opponent"
              />
            </div>

            {/* Unassigned players */}
            {unassigned.length > 0 && (
              <div className="border-y border-dashed border-border py-3">
                <p className="mb-2 text-center text-xs text-text-secondary">
                  Unassigned
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {unassigned.map((p) => (
                    <DraggablePlayer key={p.id} participant={p} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <DragOverlay>
            {activeParticipant && (
              <PlayerCard participant={activeParticipant} isDragging />
            )}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// BUILD DISPLAY DATA FOR TEMPLATES
// ──────────────────────────────────────────────────────────────────────────────

// Map persisted team ids to the viewer-relative names expected by templates.
function toDisplayPositions(
  positions: Record<string, "team1" | "team2">,
): Record<string, "your-team" | "opponent-team"> {
  const result: Record<string, "your-team" | "opponent-team"> = {};
  for (const [id, team] of Object.entries(positions)) {
    result[id] = team === "team1" ? "your-team" : "opponent-team";
  }
  return result;
}

function buildDisplayData(
  activityId: string,
  sessionFormat: ActivitySessionFormat,
  positions: Record<string, "team1" | "team2">,
  scoreData: Record<string, unknown> | null,
): unknown {
  // Convert to Display component format
  const basePositions = toDisplayPositions(positions);

  // New (template-first) scoring stores `matchScore` + `positions` directly.
  // If present, prefer it and only backfill missing positions.
  const hasTemplateMatchScore =
    scoreData != null &&
    typeof scoreData === "object" &&
    !Array.isArray(scoreData) &&
    scoreData.matchScore != null &&
    typeof scoreData.matchScore === "object" &&
    !Array.isArray(scoreData.matchScore);

  if (hasTemplateMatchScore) {
    const positionsRaw = (scoreData as Record<string, unknown>).positions;
    const mergedPositions =
      positionsRaw &&
      typeof positionsRaw === "object" &&
      !Array.isArray(positionsRaw)
        ? (positionsRaw as Record<string, unknown>)
        : basePositions;

    return {
      ...(scoreData as Record<string, unknown>),
      positions: mergedPositions,
      hasScore: true,
    };
  }

  const hasTeamScore =
    scoreData != null &&
    typeof scoreData.team1Score === "number" &&
    typeof scoreData.team2Score === "number";
  const team1Score = hasTeamScore ? (scoreData.team1Score as number) : 0;
  const team2Score = hasTeamScore ? (scoreData.team2Score as number) : 0;
  const team1Wickets =
    typeof scoreData?.team1Wickets === "number" ? scoreData.team1Wickets : 0;
  const team2Wickets =
    typeof scoreData?.team2Wickets === "number" ? scoreData.team2Wickets : 0;

  switch (activityId.replace(/-/g, "")) {
    case "tennis":
      return {
        mode: sessionFormat === "doubles" ? "doubles" : "singles",
        matchScore: { yourSets: 0, opponentSets: 0 },
        sets: [],
        positions: basePositions,
        hasScore: false,
      } satisfies TennisTemplateData;

    case "badminton":
      return {
        mode: sessionFormat === "doubles" ? "doubles" : "singles",
        matchScore: { yourGames: 0, opponentGames: 0 },
        games: [],
        positions: basePositions,
        hasScore: false,
      } satisfies BadmintonTemplateData;

    case "tabletennis":
      return {
        mode: sessionFormat === "doubles" ? "doubles" : "singles",
        matchScore: { yourGames: 0, opponentGames: 0 },
        positions: basePositions,
        hasScore: false,
      } satisfies TableTennisTemplateData;

    case "padel":
      return {
        matchScore: { yourSets: 0, opponentSets: 0 },
        positions: basePositions,
        hasScore: false,
      } satisfies PadelTemplateData;

    case "pickleball":
      return {
        mode: sessionFormat === "doubles" ? "doubles" : "singles",
        matchScore: { yourGames: 0, opponentGames: 0 },
        positions: basePositions,
        hasScore: false,
      } satisfies PickleballTemplateData;

    case "squash":
      return {
        matchScore: { yourGames: 0, opponentGames: 0 },
        positions: basePositions,
        hasScore: false,
      } satisfies SquashTemplateData;

    case "snooker":
      return {
        mode: sessionFormat === "doubles" ? "doubles" : "singles",
        matchScore: { yourFrames: 0, opponentFrames: 0 },
        positions: basePositions,
        hasScore: false,
      } satisfies SnookerTemplateData;

    case "football":
      return {
        matchScore: { yourGoals: team1Score, opponentGoals: team2Score },
        positions: basePositions,
        hasScore: hasTeamScore,
      } satisfies FootballTemplateData;

    case "basketball":
      return {
        matchScore: { yourPoints: team1Score, opponentPoints: team2Score },
        positions: basePositions,
        hasScore: hasTeamScore,
      } satisfies BasketballTemplateData;

    case "cricket": {
      if (!hasTeamScore) {
        return {
          yourInnings: { runs: 0, wickets: 0 },
          opponentInnings: { runs: 0, wickets: 0 },
          result: null,
          positions: basePositions,
          hasScore: false,
        } satisfies CricketTemplateData;
      }

      const cricketResult: CricketTemplateData["result"] =
        team1Score > team2Score
          ? "won-by-runs"
          : team2Score > team1Score
            ? "loss"
            : "tie";

      return {
        yourInnings: { runs: team1Score, wickets: team1Wickets },
        opponentInnings: { runs: team2Score, wickets: team2Wickets },
        result: cricketResult,
        positions: basePositions,
        hasScore: true,
      } satisfies CricketTemplateData;
    }

    case "volleyball":
      return {
        matchScore: { yourSets: team1Score, opponentSets: team2Score },
        positions: basePositions,
        hasScore: hasTeamScore,
      } satisfies VolleyballTemplateData;

    case "throwball":
      return {
        matchScore: { yourSets: team1Score, opponentSets: team2Score },
        positions: basePositions,
        hasScore: hasTeamScore,
      } satisfies ThrowballTemplateData;

    default:
      return { positions: basePositions };
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// DRAG & DROP COMPONENTS
// ──────────────────────────────────────────────────────────────────────────────

type TeamDropZoneProps = {
  id: "team1" | "team2";
  label: string;
  participants: TeamParticipant[];
  variant: "your" | "opponent";
};

function TeamDropZone({ id, label, participants, variant }: TeamDropZoneProps) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "border-2 px-3 py-4 transition-colors",
        variant === "your" ? "border-brand/30" : "border-text-secondary/30",
        isOver && "border-brand bg-brand/10",
      )}
    >
      <div
        className={cn(
          "mb-2 text-center text-xs font-semibold",
          variant === "your"
            ? "text-brand dark:text-violet-300"
            : "text-text-secondary",
        )}
      >
        {label}
      </div>
      <div className="flex min-h-[48px] flex-wrap justify-center gap-2">
        {participants.map((p) => (
          <DraggablePlayer key={p.id} participant={p} variant={variant} />
        ))}
        {participants.length === 0 && (
          <span className="text-xs text-text-tertiary">Drop here</span>
        )}
      </div>
    </div>
  );
}

type DraggablePlayerProps = {
  participant: TeamParticipant;
  variant?: "your" | "opponent";
};

function DraggablePlayer({ participant, variant }: DraggablePlayerProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: participant.id,
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={cn(
        "cursor-grab touch-none active:cursor-grabbing",
        isDragging && "opacity-30",
      )}
    >
      <PlayerCard participant={participant} variant={variant} />
    </div>
  );
}

type PlayerCardProps = {
  participant: TeamParticipant;
  variant?: "your" | "opponent";
  isDragging?: boolean;
};

function PlayerCard({ participant, variant, isDragging }: PlayerCardProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center",
        isDragging && "scale-110 shadow-xl",
      )}
    >
      <Avatar
        className={cn(
          "h-10 w-10 shadow-md ring-2",
          variant === "your"
            ? "ring-brand"
            : variant === "opponent"
              ? "ring-text-secondary"
              : "ring-border",
          isDragging && "ring-4 ring-brand",
        )}
      >
        <AvatarImage src={participant.avatarUrl ?? undefined} />
        <AvatarFallback
          className={cn(
            "text-xs font-bold",
            variant === "your"
              ? "bg-brand text-white"
              : variant === "opponent"
                ? "bg-text-secondary text-white"
                : "bg-surface-alt text-text-primary",
          )}
        >
          {participant.displayName.slice(0, 2).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <span className="text-2xs mt-1 max-w-[70px] truncate text-center text-text-secondary">
        {participant.displayName.split(" ")[0]}
      </span>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// SIMPLE PARTICIPANT LIST (for non-court activities)
// ──────────────────────────────────────────────────────────────────────────────

function SimpleParticipantList({
  participants,
}: {
  participants: TeamParticipant[];
}) {
  if (participants.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {participants.map((p) => (
        <div
          key={p.id}
          className="flex items-center gap-1.5 rounded-full bg-surface-alt px-2 py-1"
        >
          <Avatar className="h-6 w-6">
            <AvatarImage src={p.avatarUrl ?? undefined} />
            <AvatarFallback className="text-2xs">
              {p.displayName.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <span className="text-sm text-text-primary">{p.displayName}</span>
        </div>
      ))}
    </div>
  );
}
